import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send } from "lucide-react";
import { api } from "../lib/api";

const LS_KEY = "nh_chat_session";

export function ChatWidget() {
  const [config, setConfig] = useState(null);
  const [open, setOpen] = useState(false);
  const [session, setSession] = useState(() => localStorage.getItem(LS_KEY) || "");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const endRef = useRef(null);

  useEffect(() => { api.get("/public/chat/config").then((r) => setConfig(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (!session || !open) return;
    const load = () => api.get(`/public/chat/${session}/messages`).then((r) => setMessages(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [session, open]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  if (!config || !config.enabled) return null;

  const start = async () => {
    if (!name.trim()) return;
    const { data } = await api.post("/public/chat/session", { name, email });
    localStorage.setItem(LS_KEY, data.session_id);
    setSession(data.session_id);
  };

  const send = async () => {
    if (!text.trim()) return;
    const t = text; setText("");
    setMessages((m) => [...m, { id: `tmp-${Date.now()}`, sender: "visitor", text: t, created_at: new Date().toISOString() }]);
    await api.post(`/public/chat/${session}/message`, { text: t }).catch(() => {});
  };

  const askQuick = async (q) => {
    setMessages((m) => [
      ...m,
      { id: `q-${Date.now()}`, sender: "visitor", text: q.question, created_at: new Date().toISOString() },
      { id: `a-${Date.now()}`, sender: "system", text: q.answer, created_at: new Date().toISOString() },
    ]);
    await api.post(`/public/chat/${session}/message`, { text: q.question }).catch(() => {});
  };

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="chat-widget-button"
          className="fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full bg-brand text-white shadow-lg flex items-center justify-center hover:bg-brand-hover transition-colors">
          <MessageCircle className="w-6 h-6" />
        </button>
      )}
      {open && (
        <div data-testid="chat-widget-panel"
          className="fixed bottom-5 right-5 z-50 w-[90vw] max-w-sm h-[70vh] max-h-[560px] bg-white rounded-2xl shadow-2xl border border-[#E8E2D9] flex flex-col overflow-hidden">
          <div className="bg-brand text-white px-4 py-3 flex items-center justify-between">
            <div>
              <div className="font-medium text-sm">Nelleke Holt</div>
              <div className="text-[11px] flex items-center gap-1 text-white/80">
                <span className={`w-2 h-2 rounded-full ${config.online ? "bg-green-300" : "bg-white/50"}`} />
                {config.online ? "Online" : "Offline"}
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="chat-widget-close"><X className="w-5 h-5" /></button>
          </div>

          {!session ? (
            <div className="flex-1 p-5 flex flex-col justify-center gap-3">
              <p className="text-sm text-ink-muted">{config.welcome}</p>
              <input value={name} onChange={(e) => setName(e.target.value)} data-testid="chat-name-input"
                placeholder="Je naam" className="rounded-xl border border-[#E8E2D9] px-3 py-2 text-sm outline-none focus:border-brand" />
              <input value={email} onChange={(e) => setEmail(e.target.value)} data-testid="chat-email-input"
                placeholder="E-mail (optioneel)" className="rounded-xl border border-[#E8E2D9] px-3 py-2 text-sm outline-none focus:border-brand" />
              <button onClick={start} data-testid="chat-start-btn"
                className="rounded-full bg-brand text-white py-2.5 text-sm hover:bg-brand-hover transition-colors">Start gesprek</button>
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-auto p-3 space-y-2 bg-[#FAF7F2]">
                {messages.map((m) => (
                  <div key={m.id} className={`flex ${m.sender === "visitor" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${
                      m.sender === "visitor" ? "bg-brand text-white" : m.sender === "system" ? "bg-[#EFEAE2] text-ink" : "bg-white border border-[#E8E2D9]"}`}>
                      {m.text}
                    </div>
                  </div>
                ))}
                {(config.quick_replies || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {config.quick_replies.map((q, i) => (
                      <button key={i} onClick={() => askQuick(q)} data-testid={`chat-quick-${i}`}
                        className="text-xs bg-white border border-[#E8E2D9] rounded-full px-3 py-1.5 hover:bg-brand-light transition-colors text-left">
                        {q.question}
                      </button>
                    ))}
                  </div>
                )}
                <div ref={endRef} />
              </div>
              <div className="p-2.5 border-t border-[#F2EBE1] flex gap-2">
                <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
                  data-testid="chat-message-input" placeholder="Typ een bericht…"
                  className="flex-1 rounded-full border border-[#E8E2D9] px-3 py-2 text-sm outline-none focus:border-brand" />
                <button onClick={send} data-testid="chat-message-send"
                  className="w-10 h-10 rounded-full bg-brand text-white flex items-center justify-center shrink-0 hover:bg-brand-hover transition-colors"><Send className="w-4 h-4" /></button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
